'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Download,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  Building2,
  Calendar,
  Hash,
  MapPin,
  ChevronDown,
  FileText,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import { type ClaimItem } from '@/lib/claimStore';
import { fetchClaimItems } from '@/lib/claimService';
import { useClaimContext } from '@/contexts/ClaimContext';
import * as XLSX from 'xlsx';

const ClaimValueChart = dynamic(() => import('./ClaimValueChart'), { ssr: false });
const ItemsAddedChart = dynamic(() => import('./ItemsAddedChart'), { ssr: false });

interface ExportFormValues {
  claimNumber: string;
  insuranceCompany: string;
  policyNumber: string;
  dateOfLoss: string;
  propertyAddress: string;
  adjusterName: string;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export default function ExportContent() {
  const { claims, activeClaim, loadingClaims } = useClaimContext();
  const [selectedClaimId, setSelectedClaimId] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [items, setItems] = useState<ClaimItem[]>([]);
  const [loading, setLoading] = useState(true);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ExportFormValues>({
    defaultValues: {
      claimNumber: '',
      insuranceCompany: '',
      policyNumber: '',
      dateOfLoss: '',
      propertyAddress: '',
      adjusterName: '',
    },
  });

  // Set default selected claim to active claim once claims are loaded
  useEffect(() => {
    if (!loadingClaims && claims.length > 0 && !selectedClaimId) {
      const defaultId = activeClaim?.id ?? claims[0].id;
      setSelectedClaimId(defaultId);
    }
  }, [loadingClaims, claims, activeClaim, selectedClaimId]);

  const selectedClaim = claims.find((c) => c.id === selectedClaimId) ?? null;

  const loadClaimData = useCallback(async (claimId: string) => {
    if (!claimId) return;
    setLoading(true);
    const itemData = await fetchClaimItems(claimId);
    setItems(itemData);
    setLoading(false);
  }, []);

  // When selected claim changes, reload items and reset form
  useEffect(() => {
    if (!selectedClaimId) return;
    loadClaimData(selectedClaimId);
  }, [selectedClaimId, loadClaimData]);

  // When selected claim object changes, reset form fields
  useEffect(() => {
    if (selectedClaim) {
      reset({
        claimNumber: selectedClaim.claimNumber,
        insuranceCompany: selectedClaim.insuranceCompany,
        policyNumber: selectedClaim.policyNumber,
        dateOfLoss: selectedClaim.dateOfLoss,
        propertyAddress: selectedClaim.propertyAddress,
        adjusterName: selectedClaim.adjusterName,
      });
    }
  }, [selectedClaim?.id, reset]);

  // Compute summary data
  const totalValue = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  const pricedItems = items.filter((i) => i.unitPrice > 0);
  const unpricedCount = items.filter((i) => i.priceStatus === 'unpriced').length;

  // Room breakdown
  const roomMap = new Map<string, { count: number; value: number }>();
  items.forEach((item) => {
    const existing = roomMap.get(item.location) || { count: 0, value: 0 };
    roomMap.set(item.location, {
      count: existing.count + item.quantity,
      value: existing.value + item.unitPrice * item.quantity,
    });
  });
  const roomData = Array.from(roomMap.entries())
    .map(([room, data]) => ({ room, ...data }))
    .sort((a, b) => b.value - a.value);

  const chartData = roomData.map((r) => ({ room: r.room, value: r.value, count: r.count }));

  // Member breakdown
  const memberMap = new Map<string, { count: number; value: number }>();
  items.forEach((item) => {
    const existing = memberMap.get(item.addedBy) || { count: 0, value: 0 };
    memberMap.set(item.addedBy, {
      count: existing.count + 1,
      value: existing.value + item.unitPrice * item.quantity,
    });
  });
  const memberData = Array.from(memberMap.entries()).map(([name, data]) => ({ name, ...data }));

  // Timeline data
  const dateMap = new Map<string, number>();
  items.forEach((item) => {
    dateMap.set(item.dateAdded, (dateMap.get(item.dateAdded) || 0) + 1);
  });
  const sortedDates = Array.from(dateMap.entries()).sort(([a], [b]) => a.localeCompare(b));
  let cumulative = 0;
  const timelineData = sortedDates.map(([date, count]) => {
    cumulative += count;
    const [y, m, d] = date.split('-');
    return { date: `${m}/${d}`, count, cumulative };
  });

  const handleExport = handleSubmit(async (formData) => {
    setIsExporting(true);
    await new Promise((r) => setTimeout(r, 800));

    const wb = XLSX.utils.book_new();

    const inventoryRows = items.map((item) => ({
      'Item Name': item.name,
      'Description': item.description,
      'Room / Location': item.location,
      'Added By': item.addedBy,
      'Quantity': item.quantity,
      'Condition': item.condition,
      'Estimated Age': item.estimatedAge,
      'Unit Price (New)': item.unitPrice || '',
      'Total Value': item.unitPrice * item.quantity || '',
      'Price Status': item.priceStatus,
      'Price Source': item.priceResearch?.sourceName || '',
      'Source URL': item.priceResearch?.sourceUrl || '',
      'Research Date': item.priceResearch?.researchDate || '',
      'Confidence': item.priceResearch?.confidence || '',
      'Notes': item.notes,
      'Date Added': item.dateAdded,
      'Input Method': item.inputMethod,
    }));

    const ws = XLSX.utils.json_to_sheet(inventoryRows);
    ws['!cols'] = [
      { wch: 35 }, { wch: 45 }, { wch: 18 }, { wch: 18 },
      { wch: 8 }, { wch: 18 }, { wch: 14 }, { wch: 16 },
      { wch: 14 }, { wch: 12 }, { wch: 20 }, { wch: 40 },
      { wch: 14 }, { wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');

    const summaryRows = [
      ['CLAIM INFORMATION', ''],
      ['Claim Name', selectedClaim?.name ?? ''],
      ['Claim Number', formData.claimNumber],
      ['Insurance Company', formData.insuranceCompany],
      ['Policy Number', formData.policyNumber],
      ['Date of Loss', formData.dateOfLoss],
      ['Property Address', formData.propertyAddress],
      ['Adjuster Name', formData.adjusterName],
      ['', ''],
      ['CLAIM TOTALS', ''],
      ['Total Items Documented', items.length],
      ['Total Claim Value', totalValue],
      ['Items with Verified Price', pricedItems.length],
      ['Items Needing Price Research', unpricedCount],
      ['', ''],
      ['BREAKDOWN BY ROOM', ''],
      ['Room', 'Item Count', 'Total Value'],
      ...roomData.map((r) => [r.room, r.count, r.value]),
      ['', ''],
      ['BREAKDOWN BY FAMILY MEMBER', ''],
      ['Member', 'Items Added', 'Total Value'],
      ...memberData.map((m) => [m.name, m.count, m.value]),
    ];

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Claim Summary');

    const filename = `ClaimTracker_${(formData.claimNumber || selectedClaim?.name || 'claim').replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);

    setIsExporting(false);
    toast.success(`Exported ${items.length} items to ${filename}`);
  });

  if (loadingClaims) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-sm text-muted-foreground">Loading claims…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Export &amp; Claim Summary</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review your documented inventory and export to Excel for your insurance adjuster
          </p>
        </div>
        {/* Claim selector */}
        <div className="shrink-0">
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            Generating report for:
          </label>
          <div className="relative">
            <FileText size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={selectedClaimId}
              onChange={(e) => setSelectedClaimId(e.target.value)}
              className="pl-8 pr-8 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring appearance-none min-w-[220px] font-medium text-foreground"
            >
              {claims.map((claim) => (
                <option key={claim.id} value={claim.id}>
                  {claim.name}{claim.claimNumber ? ` — #${claim.claimNumber}` : ''}
                  {claim.isActive ? ' (active)' : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Claim identity banner */}
      {selectedClaim && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-primary/5 border border-primary/20 rounded-xl text-sm">
          <span className="font-semibold text-primary">{selectedClaim.name}</span>
          {selectedClaim.claimNumber && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Hash size={12} /> Claim #{selectedClaim.claimNumber}
            </span>
          )}
          {selectedClaim.insuranceCompany && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Building2 size={12} /> {selectedClaim.insuranceCompany}
            </span>
          )}
          {selectedClaim.dateOfLoss && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Calendar size={12} /> Loss: {selectedClaim.dateOfLoss}
            </span>
          )}
          {selectedClaim.isActive && (
            <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-positive-bg text-positive rounded-full">
              Active Claim
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-muted-foreground">Loading claim data…</span>
        </div>
      ) : (
        <>
          {/* Summary hero cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 bg-primary rounded-xl p-5 text-primary-foreground relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-6 translate-x-6" />
              <p className="text-xs font-medium uppercase tracking-widest opacity-80 mb-1">Total Claim Value</p>
              <p className="text-xs opacity-60 mb-2 truncate">{selectedClaim?.name}</p>
              <p className="text-4xl font-bold tabular-nums font-mono">{formatCurrency(totalValue)}</p>
              <p className="text-xs opacity-70 mt-1">{items.length} items documented</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Priced Items</p>
              <p className="text-2xl font-bold text-foreground tabular-nums">{pricedItems.length}<span className="text-sm font-normal text-muted-foreground">/{items.length}</span></p>
              <p className="text-xs text-muted-foreground mt-1">with source citations</p>
            </div>
            {unpricedCount > 0 ? (
              <div className="bg-negative-bg border border-negative/20 rounded-xl p-4">
                <p className="text-xs font-medium text-negative uppercase tracking-wide mb-2">⚠ Unpriced</p>
                <p className="text-2xl font-bold text-negative tabular-nums">{unpricedCount}</p>
                <p className="text-xs text-negative/70 mt-1">items need pricing</p>
              </div>
            ) : (
              <div className="bg-positive-bg border border-positive/20 rounded-xl p-4">
                <p className="text-xs font-medium text-positive uppercase tracking-wide mb-2">✓ All Priced</p>
                <p className="text-2xl font-bold text-positive tabular-nums">100%</p>
                <p className="text-xs text-positive/70 mt-1">items have pricing</p>
              </div>
            )}
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-sm font-semibold text-foreground mb-4">Claim Value by Room</h2>
              <ClaimValueChart data={chartData} />
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-sm font-semibold text-foreground mb-4">Items Documented Over Time</h2>
              <ItemsAddedChart data={timelineData} />
            </div>
          </div>

          {/* Room + Member breakdown tables */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Room breakdown */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border bg-secondary/30">
                <h2 className="text-sm font-semibold text-foreground">Breakdown by Room</h2>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Room</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Items</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Value</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">% of Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {roomData.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No items yet</td></tr>
                  ) : roomData.map((row, idx) => (
                    <tr key={`room-row-${row.room}`} className={idx % 2 === 0 ? 'bg-card' : 'bg-secondary/20'}>
                      <td className="px-4 py-2.5 text-sm text-foreground font-medium">{row.room}</td>
                      <td className="px-4 py-2.5 text-sm text-foreground text-right tabular-nums font-mono">{row.count}</td>
                      <td className="px-4 py-2.5 text-sm text-foreground text-right tabular-nums font-mono font-semibold">{formatCurrency(row.value)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full"
                              style={{ width: totalValue > 0 ? `${Math.round((row.value / totalValue) * 100)}%` : '0%' }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground tabular-nums w-8 text-right">
                            {totalValue > 0 ? Math.round((row.value / totalValue) * 100) : 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-secondary/40">
                    <td className="px-4 py-2.5 text-sm font-bold text-foreground">Total</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-foreground text-right tabular-nums font-mono">{items.length}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-primary text-right tabular-nums font-mono">{formatCurrency(totalValue)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Member breakdown */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border bg-secondary/30">
                <h2 className="text-sm font-semibold text-foreground">Breakdown by Family Member</h2>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Member</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Items Added</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Value Documented</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {memberData.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-muted-foreground">No items yet</td></tr>
                  ) : memberData.map((row, idx) => (
                    <tr key={`member-row-${row.name}`} className={idx % 2 === 0 ? 'bg-card' : 'bg-secondary/20'}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                            {row.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                          </div>
                          <span className="text-sm text-foreground font-medium">{row.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-foreground text-right tabular-nums font-mono">{row.count}</td>
                      <td className="px-4 py-2.5 text-sm font-semibold text-foreground text-right tabular-nums font-mono">{formatCurrency(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Export configuration form */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border bg-secondary/30 flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Export Configuration</h2>
              {selectedClaim && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                  {selectedClaim.name}
                </span>
              )}
            </div>
            <div className="p-5 space-y-5">
              <p className="text-sm text-muted-foreground">
                Verify the claim details below before exporting. These will appear in the Excel summary sheet.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label htmlFor="claimNumber" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Hash size={13} className="text-muted-foreground" /> Claim Number
                  </label>
                  <input
                    id="claimNumber"
                    type="text"
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono ${errors.claimNumber ? 'border-negative' : 'border-border'}`}
                    {...register('claimNumber', { required: 'Claim number is required' })}
                  />
                  {errors.claimNumber && <p className="text-xs text-negative">{errors.claimNumber.message}</p>}
                </div>
                <div className="space-y-1">
                  <label htmlFor="insuranceCompany" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Building2 size={13} className="text-muted-foreground" /> Insurance Company
                  </label>
                  <input
                    id="insuranceCompany"
                    type="text"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                    {...register('insuranceCompany')}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="policyNumber" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Hash size={13} className="text-muted-foreground" /> Policy Number
                  </label>
                  <input
                    id="policyNumber"
                    type="text"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                    {...register('policyNumber')}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="dateOfLoss" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Calendar size={13} className="text-muted-foreground" /> Date of Loss
                  </label>
                  <input
                    id="dateOfLoss"
                    type="date"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                    {...register('dateOfLoss')}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="adjusterName" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Adjuster Name
                  </label>
                  <input
                    id="adjusterName"
                    type="text"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                    {...register('adjusterName')}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="propertyAddress" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <MapPin size={13} className="text-muted-foreground" /> Property Address
                  </label>
                  <input
                    id="propertyAddress"
                    type="text"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                    {...register('propertyAddress')}
                  />
                </div>
              </div>

              {/* Excel preview */}
              <div className="bg-secondary/30 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-foreground">Excel Export Preview — Column Structure</p>
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full min-w-[800px] text-xs">
                    <thead>
                      <tr className="bg-primary/10 border border-primary/20 rounded">
                        {[
                          'Item Name', 'Description', 'Room', 'Added By', 'Qty',
                          'Condition', 'Age', 'Unit Price', 'Total Value', 'Status',
                          'Source', 'Source URL', 'Notes', 'Date Added',
                        ].map((col) => (
                          <th key={`col-${col}`} className="px-2 py-1.5 text-left font-semibold text-primary whitespace-nowrap border-r border-primary/10 last:border-r-0">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.slice(0, 3).map((item) => (
                        <tr key={`preview-${item.id}`} className="border-b border-border/50">
                          <td className="px-2 py-1.5 text-foreground truncate max-w-[120px]">{item.name}</td>
                          <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[140px]">{item.description.slice(0, 30)}{item.description.length > 30 ? '…' : ''}</td>
                          <td className="px-2 py-1.5 text-foreground whitespace-nowrap">{item.location}</td>
                          <td className="px-2 py-1.5 text-foreground whitespace-nowrap">{item.addedBy.split(' ')[0]}</td>
                          <td className="px-2 py-1.5 text-foreground text-center font-mono">{item.quantity}</td>
                          <td className="px-2 py-1.5 text-foreground whitespace-nowrap">{item.condition}</td>
                          <td className="px-2 py-1.5 text-foreground whitespace-nowrap">{item.estimatedAge}</td>
                          <td className="px-2 py-1.5 text-foreground font-mono whitespace-nowrap">{item.unitPrice ? formatCurrency(item.unitPrice) : '—'}</td>
                          <td className="px-2 py-1.5 text-foreground font-mono font-semibold whitespace-nowrap">{item.unitPrice ? formatCurrency(item.unitPrice * item.quantity) : '—'}</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${item.priceStatus === 'priced' || item.priceStatus === 'verified' ? 'bg-positive-bg text-positive' : 'bg-negative-bg text-negative'}`}>
                              {item.priceStatus}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{item.priceResearch?.sourceName || '—'}</td>
                          <td className="px-2 py-1.5 text-info truncate max-w-[100px]">{item.priceResearch?.sourceUrl ? 'link' : '—'}</td>
                          <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[100px]">{item.notes.slice(0, 20)}{item.notes.length > 20 ? '…' : ''}</td>
                          <td className="px-2 py-1.5 text-muted-foreground font-mono whitespace-nowrap">{item.dateAdded}</td>
                        </tr>
                      ))}
                      {items.length > 3 && (
                        <tr className="border-b border-border/50">
                          <td colSpan={14} className="px-2 py-1.5 text-center text-muted-foreground italic">
                            … {items.length - 3} more rows
                          </td>
                        </tr>
                      )}
                      {items.length === 0 && (
                        <tr>
                          <td colSpan={14} className="px-2 py-4 text-center text-muted-foreground italic">
                            No items yet — add items to see preview
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Export button */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={isExporting || items.length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
                >
                  {isExporting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Generating Excel…
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Download Excel — {selectedClaim?.name ?? 'Claim'}
                    </>
                  )}
                </button>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <p className="flex items-center gap-1">
                    <CheckCircle size={11} className="text-positive" />
                    Includes all {items.length} items with pricing and source citations
                  </p>
                  <p className="flex items-center gap-1">
                    <CheckCircle size={11} className="text-positive" />
                    Two sheets: full inventory + claim summary
                  </p>
                  <p className="flex items-center gap-1">
                    <CheckCircle size={11} className="text-positive" />
                    Ready to email directly to your insurance adjuster
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}